import { AllMatches } from "./components/AllMatches";
import { Leaderboard } from "./components/Leaderboard";
import { LoginModal } from "./components/LoginModal";
import { MatchHistory } from "./components/MatchHistory";
import { MatchRecorder } from "./components/MatchRecorder";
import { PlayerManager } from "./components/PlayerManager";
import { PlayerStats } from "./components/PlayerStats";
import { ThemeSwitcher } from "./components/ThemeSwitcher";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useEloTracker } from "./hooks/useEloTracker";
import { useRouter } from "./hooks/useRouter";
import "./App.css";

function AppContent() {
	const { isAuthenticated, isAdmin, role, logout } = useAuth();
	const { pathname, navigate } = useRouter();
	const {
		players,
		sortedPlayers,
		matches,
		addPlayer,
		removePlayer,
		recordMatch,
		undoLastMatch,
		resetAll,
	} = useEloTracker();

	if (!isAuthenticated) {
		return <LoginModal />;
	}

	// Parse route
	const playerStatsMatch = pathname.match(/^\/players\/(.+)$/);
	const isPlayersPage = pathname === "/players";
	const isMatchesPage = pathname === "/matches";
	const selectedPlayerId = playerStatsMatch?.[1] ?? null;

	// Force non-admins away from /players
	const effectivePage = isMatchesPage
		? "matches"
		: !isAdmin && isPlayersPage
			? "play"
			: isPlayersPage
				? "players"
				: selectedPlayerId
					? "stats"
					: "play";

	return (
		<div className="app">
			<nav className="tabs">
				<span className="header-emoji">🪨📄✂️</span>
				<div className="auth-info">
					<ThemeSwitcher />
					<span className="role-badge">{role}</span>
					<button type="button" onClick={logout} className="btn-logout">
						Logout
					</button>
				</div>
			</nav>
			<main>
				{effectivePage === "matches" ? (
					<AllMatches onBack={() => navigate("/")} />
				) : effectivePage === "stats" && selectedPlayerId ? (
					<div className="full-width">
						<PlayerStats
							playerId={selectedPlayerId}
							onBack={() => navigate("/")}
						/>
					</div>
				) : effectivePage === "play" ? (
					<>
						<div className="left-column">
							<MatchRecorder
								players={players}
								onRecordMatch={recordMatch}
								onUndo={undoLastMatch}
								canUndo={matches.length > 0}
							/>
							<MatchHistory
								matches={matches}
								players={players}
								onViewAll={() => navigate("/matches")}
							/>
						</div>
						<div className="right-column">
							<Leaderboard
								players={sortedPlayers}
								onPlayerClick={(id) => navigate(`/players/${id}`)}
							/>
						</div>
					</>
				) : (
					<div className="full-width">
						<button
							type="button"
							onClick={() => navigate("/")}
							className="btn-back"
						>
							← Leaderboard
						</button>
						<PlayerManager
							players={players}
							onAddPlayer={addPlayer}
							onRemovePlayer={removePlayer}
						/>
					</div>
				)}
			</main>
			{isAdmin && (
				<footer className="app-footer">
					<button
						type="button"
						className={effectivePage === "players" ? "active" : ""}
						onClick={() => navigate("/players")}
					>
						Players ({players.length})
					</button>
					{players.length > 0 && (
						<button
							type="button"
							onClick={() => {
								if (
									window.confirm(
										"Are you sure? This will delete all players and match history.",
									)
								) {
									resetAll();
								}
							}}
							className="btn-reset"
						>
							Reset All
						</button>
					)}
				</footer>
			)}
		</div>
	);
}

function App() {
	return (
		<ThemeProvider>
			<AuthProvider>
				<AppContent />
			</AuthProvider>
		</ThemeProvider>
	);
}

export default App;
